import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import type {
  ScheduleDetail,
  ScheduleDraftInput,
  ScheduleEditOptions,
  ScheduleListItem,
  ScheduleUpdateDraftInput
} from '../appService';
import {
  buildCalendarDateKeys,
  formatAttendanceRecordStatus,
  formatAttendanceSelectedTime,
  formatDateKey,
  formatScheduleDateChip,
  formatScheduleDetailTime,
  formatScheduleFullDate,
  formatScheduleMonthKey,
  formatScheduleMonthLabel,
  formatScheduleRowMeta,
  getAdjacentScheduleMonth,
  getScheduleDateKey,
  getUtcMonthDate,
  groupSchedulesByDate,
  sortSchedulesByStart
} from '../scheduleView';
import type { ScheduleMode } from '../workspaceRoutes';
import { useAppRuntime, useWorkspaceBootstrapContext, useWorkspaceContext, useWorkspaceRouteAdapter } from './context';
import {
  invalidateScheduleMonths,
  invalidateScheduleUpcomingQueries,
  scheduleDetailQueryKey,
  scheduleHomeUpcomingQueryKey,
  scheduleMonthQueryKey,
  usePrefetchAdjacentScheduleMonths,
  useScheduleDetailQuery,
  useScheduleEditOptionsQuery,
  useScheduleMonthQuery,
  useScheduleUpcomingQuery
} from './data';
import { useScheduleDraftStore } from './draftStores';
import {
  ActionError,
  BackHeader,
  CompactEmptyRow,
  ConfirmedField,
  DiscardUnsavedChangesDialog,
  LoadingAffordance,
  ModuleEmptyState,
  PendingButton,
  RecoverableReadError,
  SectionLoading,
  SvgIcon,
  TextField,
  WorkspaceModuleTopBar,
  formatActionError,
  formatMemberGroupNames
} from './ui';

const SCHEDULE_UPCOMING_LIST_LIMIT = 5;
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MAX_TIMER_DELAY_MS = 2_147_483_647;

type WorkspaceScheduleTabSearch = {
  readonly mode?: ScheduleMode;
  readonly date?: string;
  readonly month?: string;
};

export function WorkspaceScheduleTab({ search }: { readonly search: WorkspaceScheduleTabSearch }): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const mode = search.mode ?? 'list';
  const selectedDate = mode === 'calendar' ? search.date : undefined;
  const monthKey = getScheduleMonthKeyFromSearch(search);
  const canCreateSchedule = appContext.currentPermissionKeys.includes('schedule_manage');

  return (
    <>
      <WorkspaceModuleTopBar title="日程" />
      <ScheduleModeToggle mode={mode} monthKey={monthKey} selectedDate={selectedDate} />
      {mode === 'calendar' ? (
        <ScheduleCalendar monthKey={monthKey} selectedDate={selectedDate} canCreateSchedule={canCreateSchedule} />
      ) : (
        <ScheduleListContent />
      )}
    </>
  );
}

function ScheduleModeToggle({
  mode,
  monthKey,
  selectedDate
}: {
  readonly mode: ScheduleMode;
  readonly monthKey: string;
  readonly selectedDate: string | undefined;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const items: readonly {
    readonly mode: ScheduleMode;
    readonly action: string;
    readonly label: string;
  }[] = [
    { mode: 'list', action: 'switch-schedule-list', label: '日程表' },
    { mode: 'calendar', action: 'switch-schedule-calendar', label: '月历' }
  ];

  return (
    <nav className="schedule-mode-toggle" aria-label="日程视图">
      {items.map((item) => (
        <button
          type="button"
          className={item.mode === mode ? 'active' : ''}
          data-action={item.action}
          aria-current={item.mode === mode ? 'page' : undefined}
          key={item.mode}
          onClick={() => {
            if (item.mode === mode) {
              return;
            }
            routeAdapter.replaceScheduleRoot({
              mode: item.mode,
              monthKey,
              date: item.mode === 'calendar' ? selectedDate : undefined
            });
          }}
        >
          {item.label}
        </button>
      ))}
    </nav>
  );
}

function ScheduleListContent(): ReactNode {
  const schedules = useScheduleUpcomingQuery(SCHEDULE_UPCOMING_LIST_LIMIT);

  if (schedules.status === 'pending') {
    return <SectionLoading body="正在读取日程。" />;
  }

  if (schedules.status === 'error') {
    return (
      <RecoverableReadError
        error={schedules.error}
        retryAction="retry-schedule-list"
        onRetry={() => void schedules.refetch()}
      />
    );
  }

  const sortedSchedules = sortSchedulesByStart(schedules.data);
  if (sortedSchedules.length === 0) {
    return <ModuleEmptyState icon="calendar" body="暂无日程" />;
  }

  return <ScheduleRows schedules={sortedSchedules} interactive />;
}

function ScheduleCalendar({
  monthKey,
  selectedDate,
  canCreateSchedule
}: {
  readonly monthKey: string;
  readonly selectedDate: string | undefined;
  readonly canCreateSchedule: boolean;
}): ReactNode {
  const month = useScheduleMonthQuery(monthKey);
  usePrefetchAdjacentScheduleMonths(monthKey, month.status === 'success');
  const routeAdapter = useWorkspaceRouteAdapter();
  const visibleMonthDate = `${monthKey}-01`;
  const visibleMonth = getUtcMonthDate(visibleMonthDate);
  const schedules = month.status === 'success' ? sortSchedulesByStart(month.data) : [];
  const schedulesByDate = groupSchedulesByDate(schedules);

  return (
    <>
      <section className="schedule-calendar-panel">
        <header className="calendar-month-header">
          <button
            type="button"
            className="calendar-month-button"
            data-action="previous-schedule-month"
            aria-label="上个月"
            onClick={() =>
              routeAdapter.replaceScheduleRoot({
                mode: 'calendar',
                monthKey: getAdjacentScheduleMonth(monthKey, -1)
              })
            }
          >
            <SvgIcon name="chevronLeft" className="calendar-month-button-icon" />
          </button>
          <h2>{formatScheduleMonthLabel(visibleMonthDate)}</h2>
          <button
            type="button"
            className="calendar-month-button"
            data-action="next-schedule-month"
            aria-label="下个月"
            onClick={() =>
              routeAdapter.replaceScheduleRoot({
                mode: 'calendar',
                monthKey: getAdjacentScheduleMonth(monthKey, 1)
              })
            }
          >
            <SvgIcon name="chevronRight" className="calendar-month-button-icon" />
          </button>
        </header>
        {month.status === 'error' ? (
          <RecoverableReadError
            error={month.error}
            retryAction="retry-schedule-month"
            onRetry={() => void month.refetch()}
          />
        ) : (
          <CalendarGrid
            monthKey={monthKey}
            visibleMonth={visibleMonth}
            selectedDate={selectedDate}
            schedulesByDate={schedulesByDate}
            isLoading={month.status === 'pending'}
          />
        )}
      </section>
      {month.status === 'error' ? null : (
        <CalendarSelectedDate
          selectedDate={selectedDate}
          schedulesByDate={schedulesByDate}
          isLoading={month.status === 'pending'}
          canCreateSchedule={canCreateSchedule}
        />
      )}
    </>
  );
}

function CalendarGrid({
  monthKey,
  visibleMonth,
  selectedDate,
  schedulesByDate,
  isLoading
}: {
  readonly monthKey: string;
  readonly visibleMonth: ReturnType<typeof getUtcMonthDate>;
  readonly selectedDate: string | undefined;
  readonly schedulesByDate: ReadonlyMap<string, readonly ScheduleListItem[]>;
  readonly isLoading: boolean;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();

  return (
    <div className="calendar-grid" role="grid">
      {['日', '一', '二', '三', '四', '五', '六'].map((label) => (
        <span className="calendar-weekday" key={label}>
          {label}
        </span>
      ))}
      {isLoading
        ? Array.from({ length: 42 }, (_, index) => (
            <button
              type="button"
              className="calendar-day calendar-loading-skeleton"
              aria-hidden="true"
              disabled
              key={index}
            />
          ))
        : buildCalendarDateKeys(visibleMonth.year, visibleMonth.month).map((dateKey) => {
            const date = getUtcMonthDate(dateKey);
            const daySchedules = schedulesByDate.get(dateKey) ?? [];
            return (
              <button
                type="button"
                className={[
                  'calendar-day',
                  date.month === visibleMonth.month ? '' : 'outside-month',
                  dateKey === selectedDate ? 'selected' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-action="select-schedule-date"
                data-date={dateKey}
                data-has-schedule={daySchedules.length > 0 ? 'true' : undefined}
                key={dateKey}
                onClick={() =>
                  routeAdapter.replaceScheduleRoot({
                    mode: 'calendar',
                    monthKey: date.month === visibleMonth.month ? monthKey : formatScheduleMonthKey(dateKey),
                    date: dateKey
                  })
                }
              >
                {date.day}
                {daySchedules.length > 0 ? <span className="calendar-day-dot" aria-hidden="true" /> : null}
              </button>
            );
          })}
    </div>
  );
}

function CalendarSelectedDate({
  selectedDate,
  schedulesByDate,
  isLoading,
  canCreateSchedule
}: {
  readonly selectedDate: string | undefined;
  readonly schedulesByDate: ReadonlyMap<string, readonly ScheduleListItem[]>;
  readonly isLoading: boolean;
  readonly canCreateSchedule: boolean;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();

  if (isLoading) {
    return (
      <section className="calendar-selected-date">
        <CompactEmptyRow body="日程加载中" />
      </section>
    );
  }

  if (!selectedDate) {
    return (
      <section className="calendar-selected-date">
        <CompactEmptyRow body="选择日期查看当天日程" />
      </section>
    );
  }

  const schedules = schedulesByDate.get(selectedDate) ?? [];
  return (
    <section className="calendar-selected-date">
      <h2>{formatScheduleFullDate(selectedDate)}</h2>
      {canCreateSchedule ? (
        <button
          type="button"
          className="secondary-button"
          data-action="open-create-schedule"
          onClick={() => routeAdapter.openScheduleCreate(selectedDate)}
        >
          新建日程
        </button>
      ) : null}
      {schedules.length === 0 ? (
        <CompactEmptyRow body="当天暂无日程" />
      ) : (
        <ScheduleRows schedules={schedules} interactive />
      )}
    </section>
  );
}

export function ScheduleRows({
  schedules,
  interactive = false
}: {
  readonly schedules: readonly ScheduleListItem[];
  readonly interactive?: boolean;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();

  return (
    <section className="schedule-list prototype-list">
      {schedules.map((schedule) => {
        const chipText = formatScheduleDateChip(schedule.startsAt);
        const rowContent = (
          <>
            <span className="schedule-date-chip">
              <strong>{chipText.day}</strong>
              <small>{chipText.weekday}</small>
            </span>
            <span className="member-copy">
              <strong>{schedule.title}</strong>
              <small>{formatScheduleRowMeta(schedule)}</small>
            </span>
            {interactive ? (
              <span className="choir-context-chevron" aria-hidden="true">
                ›
              </span>
            ) : null}
          </>
        );

        return interactive ? (
          <button
            type="button"
            className="schedule-row prototype-list-row"
            data-action="open-schedule-detail"
            data-schedule-id={schedule.scheduleId}
            aria-label={`打开日程${schedule.title}`}
            key={schedule.scheduleId}
            onClick={() => routeAdapter.openScheduleDetail(schedule.scheduleId)}
          >
            {rowContent}
          </button>
        ) : (
          <article className="schedule-display-row prototype-list-row" key={schedule.scheduleId}>
            {rowContent}
          </article>
        );
      })}
    </section>
  );
}

export function ScheduleDetailPage({ scheduleId }: { readonly scheduleId: string }): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const detail = useScheduleDetailQuery(scheduleId);

  return (
    <div className="route-outlet">
      <BackHeader
        title="日程详情"
        action="back-to-schedule"
        label="返回日程"
        onBack={() => {
          void routeAdapter.backToScheduleRoot();
        }}
      />
      <ScheduleDetailContent scheduleId={scheduleId} detail={detail} />
    </div>
  );
}

function ScheduleDetailContent({
  scheduleId,
  detail
}: {
  readonly scheduleId: string;
  readonly detail: ReturnType<typeof useScheduleDetailQuery>;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();

  if (detail.status === 'pending') {
    return <SectionLoading body="正在读取日程详情。" />;
  }

  if (detail.status === 'error') {
    return (
      <RecoverableReadError
        error={detail.error}
        retryAction="retry-schedule-detail"
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const schedule = detail.data;

  return (
    <>
      {schedule.canManage ? (
        <div className="schedule-detail-actions">
          <button
            type="button"
            className="secondary-button"
            data-action="open-edit-schedule"
            onClick={() => routeAdapter.openScheduleEdit(scheduleId)}
          >
            编辑
          </button>
          <button
            type="button"
            className="danger-button"
            data-action="open-delete-schedule-confirm"
            onClick={() => routeAdapter.openScheduleDelete(scheduleId)}
          >
            删除
          </button>
        </div>
      ) : null}
      <ScheduleDetailSummary detail={schedule} />
      <ScheduleDetailNote detail={schedule} />
      <ScheduleMyAttendance detail={schedule} />
      <ScheduleSignIn detail={schedule} />
      <ScheduleAttendanceRecords detail={schedule} />
    </>
  );
}

function ScheduleDetailSummary({ detail }: { readonly detail: ScheduleDetail }): ReactNode {
  const chipText = formatScheduleDateChip(detail.startsAt);

  return (
    <section className="schedule-detail-section schedule-detail-summary-card">
      <span className="schedule-date-chip">
        <strong>{chipText.day}</strong>
        <small>{chipText.weekday}</small>
      </span>
      <div className="schedule-detail-summary-copy">
        <h2>{detail.title}</h2>
        <p>{formatScheduleFullDate(getScheduleDateKey(detail.startsAt))}</p>
        <div className="schedule-detail-field-list">
          <ConfirmedField label="时间" value={formatScheduleDetailTime(detail)} />
          <ConfirmedField label="地点" value={detail.location} />
          <ConfirmedField label="考勤类别" value={detail.attendance?.category.name ?? '未启用考勤'} />
          {detail.attendance ? (
            <ConfirmedField
              label="参与范围"
              value={formatParticipantMemberGroups(detail.attendance.participantMemberGroups)}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}

function ScheduleDetailNote({ detail }: { readonly detail: ScheduleDetail }): ReactNode {
  return (
    <section className="schedule-detail-section schedule-detail-note-card">
      <h2>备注</h2>
      <p>{detail.note || '暂无备注'}</p>
    </section>
  );
}

function ScheduleMyAttendance({ detail }: { readonly detail: ScheduleDetail }): ReactNode {
  const record = detail.attendance?.currentUserRecord ?? null;
  let status = '未启用考勤';
  if (detail.attendance && !record) {
    status = '未参与考勤';
  }
  if (record) {
    status = formatAttendanceRecordStatus(record.status);
  }

  return (
    <section className="schedule-detail-section schedule-detail-attendance-panel">
      <h2>我的考勤状态</h2>
      <ConfirmedField label="状态" value={status} />
      {record ? <ConfirmedField label="选择时间" value={formatAttendanceSelectedTime(record.selectedAt)} /> : null}
    </section>
  );
}

function ScheduleSignIn({ detail }: { readonly detail: ScheduleDetail }): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const canSign = Boolean(detail.attendance?.currentUserRecord) && detail.attendanceStatus === 'signing';
  const signAttendance = useMutation({
    mutationKey: ['attendance', 'sign', workspaceContext, { scheduleId: detail.scheduleId }],
    mutationFn: (input: { readonly status: 'on_time' | 'late' }) =>
      service.signAttendance({
        workspaceContext,
        scheduleId: detail.scheduleId,
        status: input.status
      }),
    onSuccess: async (updatedDetail) => {
      queryClient.setQueryData(scheduleDetailQueryKey(workspaceContext, detail.scheduleId), updatedDetail);
      await queryClient.invalidateQueries({
        queryKey: scheduleMonthQueryKey(
          workspaceContext,
          formatScheduleMonthKey(getScheduleDateKey(updatedDetail.startsAt))
        ),
        exact: true
      });
      await invalidateScheduleUpcomingQueries(queryClient, workspaceContext);
    }
  });

  if (!canSign || !detail.attendance) {
    return null;
  }

  const sign = (status: 'on_time' | 'late'): void => {
    if (signAttendance.isPending) {
      return;
    }
    void (async () => {
      setActionError(null);
      try {
        await signAttendance.mutateAsync({ status });
      } catch (error) {
        setActionError(formatActionError(error));
      }
    })();
  };

  return (
    <section className="schedule-detail-section schedule-detail-sign-in-panel schedule-sign-in-section">
      <h2>签到</h2>
      <ActionError message={actionError} />
      <div className="attendance-sign-actions">
        <AttendanceSignButton
          action="sign-attendance-on-time"
          label="准时"
          reference={formatAttendanceReferenceTime(detail.startsAt, detail.attendance.onTimeReferenceMinutesFromStart)}
          variant="primary"
          pending={signAttendance.isPending}
          onClick={() => sign('on_time')}
        />
        <AttendanceSignButton
          action="sign-attendance-late"
          label="迟到"
          reference={formatAttendanceReferenceTime(detail.startsAt, detail.attendance.lateReferenceMinutesFromStart)}
          variant="secondary"
          pending={signAttendance.isPending}
          onClick={() => sign('late')}
        />
      </div>
    </section>
  );
}

function AttendanceSignButton({
  action,
  label,
  reference,
  variant,
  pending,
  onClick
}: {
  readonly action: string;
  readonly label: string;
  readonly reference: string;
  readonly variant: 'primary' | 'secondary';
  readonly pending: boolean;
  readonly onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={`attendance-sign-button ${variant === 'primary' ? 'primary-button' : 'secondary-button'}`}
      data-action={action}
      disabled={pending}
      aria-busy={pending ? 'true' : undefined}
      onClick={onClick}
    >
      <strong>{label}</strong>
      <small>{reference} 前到</small>
      {pending ? <LoadingAffordance /> : null}
    </button>
  );
}

function ScheduleAttendanceRecords({ detail }: { readonly detail: ScheduleDetail }): ReactNode {
  return (
    <section className="schedule-detail-section schedule-detail-attendance-panel">
      <h2>考勤记录</h2>
      {!detail.attendance ? (
        <p>未启用考勤</p>
      ) : detail.attendance.records.length === 0 ? (
        <CompactEmptyRow body="暂无考勤记录" />
      ) : (
        <section className="attendance-record-list prototype-list" role="list">
          {detail.attendance.records.map((record) => (
            <article
              className="attendance-record-row attendance-record-card prototype-list-row"
              data-attendance-record-id={record.attendanceRecordId}
              role="listitem"
              key={record.attendanceRecordId}
            >
              <span className="member-avatar">{record.nickname.trim().slice(0, 1) || '成'}</span>
              <span className="member-copy">
                <strong>{record.nickname}</strong>
                <small>{formatMemberGroupNames(record.memberGroupNames)}</small>
              </span>
              <span className="attendance-record-status attendance-record-status-chip">
                {formatAttendanceRecordStatus(record.status)}
              </span>
              <span className="attendance-record-time">{formatAttendanceSelectedTime(record.selectedAt)}</span>
            </article>
          ))}
        </section>
      )}
    </section>
  );
}

export function ScheduleCreatePage({ selectedDate }: { readonly selectedDate?: string }): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const routeAdapter = useWorkspaceRouteAdapter();
  const draftStore = useScheduleDraftStore();
  const editOptions = useScheduleEditOptionsQuery(Boolean(selectedDate));
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [isSubmitNavigationAllowed, setSubmitNavigationAllowed] = useState(false);
  const createSchedule = useMutation({
    mutationFn: (draft: ScheduleDraftInput) => service.createSchedule({ workspaceContext, draft }),
    onSuccess: async (createdDetail) => {
      queryClient.setQueryData(scheduleDetailQueryKey(workspaceContext, createdDetail.scheduleId), createdDetail);
      await invalidateScheduleMonths(queryClient, workspaceContext, [
        formatScheduleMonthKey(getScheduleDateKey(createdDetail.startsAt))
      ]);
      await invalidateScheduleUpcomingQueries(queryClient, workspaceContext);
      await queryClient.invalidateQueries({
        queryKey: scheduleHomeUpcomingQueryKey(workspaceContext),
        exact: true
      });
      draftStore.clearCreateDraft();
      routeAdapter.replaceScheduleDetail(createdDetail.scheduleId);
    }
  });

  const storedCreateDraft = draftStore.createDraft;
  const initialDraft = selectedDate ? buildCreateScheduleDraft(selectedDate) : null;
  const draft =
    selectedDate && storedCreateDraft && storedCreateDraft.selectedDate === selectedDate
      ? storedCreateDraft.draft
      : initialDraft;
  const isCreateDirty = Boolean(
    selectedDate && initialDraft && draft && isScheduleDraftInputChanged(initialDraft, draft)
  );
  useEffect(() => {
    if (!selectedDate || !initialDraft) {
      return;
    }
    if (draftStore.createDraft?.selectedDate !== selectedDate) {
      draftStore.setCreateDraft(selectedDate, initialDraft);
    }
  }, [draftStore, initialDraft, selectedDate]);

  if (!selectedDate || !draft) {
    return (
      <div className="route-outlet">
        <BackHeader
          title="新建日程"
          action="back-to-schedule-calendar"
          label="返回日历"
          onBack={() => routeAdapter.replaceScheduleCalendar()}
        />
        <section className="panel error" role="alert" tabIndex={-1}>
          <p>请先在月历选择日期后新建日程。</p>
        </section>
      </div>
    );
  }

  const createSelectedDate = selectedDate;
  const createDraft = draft;
  const updateDraft = (nextDraft: ScheduleDraftInput): void => {
    setActionError(null);
    draftStore.setCreateDraft(createSelectedDate, nextDraft);
  };
  const leaveCreate = (): void => {
    if (isCreateDirty) {
      setShowDiscardDialog(true);
      return;
    }
    draftStore.clearCreateDraft();
    routeAdapter.replaceScheduleCalendar(createSelectedDate);
  };

  return (
    <div className="route-outlet">
      <BackHeader
        title="新建日程"
        action="back-to-schedule-calendar"
        label="返回日历"
        pending={createSchedule.isPending}
        onBack={leaveCreate}
      />
      {showDiscardDialog ? (
        <DiscardUnsavedChangesDialog
          onKeepEditing={() => setShowDiscardDialog(false)}
          onDiscard={() => {
            setShowDiscardDialog(false);
            setSubmitNavigationAllowed(true);
            draftStore.clearCreateDraft();
            routeAdapter.replaceScheduleCalendar(createSelectedDate);
          }}
        />
      ) : null}
      <ScheduleEditorLeaveGuard
        flow={{ kind: 'create' }}
        isDirty={isCreateDirty}
        isDisabled={isSubmitNavigationAllowed}
        onDiscard={draftStore.clearCreateDraft}
      />
      <p className="schedule-editor-date">{formatScheduleFullDate(createSelectedDate)}</p>
      <ScheduleEditorContent
        draft={createDraft}
        editOptions={editOptions}
        canEditScheduleSettings
        onDraftChange={updateDraft}
        onOpenTimePicker={routeAdapter.openScheduleCreateTime}
        onOpenAttendanceSettings={routeAdapter.openScheduleCreateAttendance}
        actionError={actionError}
        saveAction="save-schedule"
        savePending={createSchedule.isPending}
        onSave={() => {
          void (async () => {
            setActionError(null);
            setSubmitNavigationAllowed(true);
            try {
              await createSchedule.mutateAsync(createDraft);
            } catch (error) {
              setSubmitNavigationAllowed(false);
              setActionError(formatActionError(error));
            }
          })();
        }}
      />
    </div>
  );
}

export function ScheduleEditPage({ scheduleId }: { readonly scheduleId: string }): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const routeAdapter = useWorkspaceRouteAdapter();
  const draftStore = useScheduleDraftStore();
  const detail = useScheduleDetailQuery(scheduleId);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [isSubmitNavigationAllowed, setSubmitNavigationAllowed] = useState(false);
  const updateSchedule = useMutation({
    mutationFn: (input: { readonly detail: ScheduleDetail; readonly draft: ScheduleDraftInput }) =>
      service.updateSchedule({
        workspaceContext,
        scheduleId,
        patch: buildScheduleUpdateDraftForEdit(input.detail, input.draft, canEditScheduleBeforeStart(input.detail))
      }),
    onSuccess: async (updatedDetail, input) => {
      queryClient.setQueryData(scheduleDetailQueryKey(workspaceContext, scheduleId), updatedDetail);
      await invalidateScheduleMonths(queryClient, workspaceContext, [
        formatScheduleMonthKey(getScheduleDateKey(input.detail.startsAt)),
        formatScheduleMonthKey(getScheduleDateKey(updatedDetail.startsAt))
      ]);
      await invalidateScheduleUpcomingQueries(queryClient, workspaceContext);
      await queryClient.invalidateQueries({
        queryKey: scheduleHomeUpcomingQueryKey(workspaceContext),
        exact: true
      });
      draftStore.clearEditDraft(scheduleId);
      routeAdapter.replaceScheduleDetail(scheduleId);
    }
  });
  const schedule = detail.status === 'success' ? detail.data : null;
  const editabilityNow = useScheduleEditabilityNow(schedule);
  const canEditScheduleSettings = schedule ? canEditScheduleBeforeStart(schedule, editabilityNow) : false;
  const editOptions = useScheduleEditOptionsQuery(canEditScheduleSettings);
  const storedDraft = draftStore.editDrafts.get(scheduleId);
  const draft = schedule ? (storedDraft ?? buildEditScheduleDraft(schedule)) : null;
  const isEditDirty = Boolean(schedule && draft && isScheduleDraftChanged(schedule, draft, canEditScheduleSettings));
  useEffect(() => {
    if (schedule && !storedDraft && draft) {
      draftStore.setEditDraft(scheduleId, draft);
    }
  }, [draft, draftStore, schedule, scheduleId, storedDraft]);

  if (detail.status === 'pending' || (canEditScheduleSettings && editOptions.status === 'pending')) {
    return (
      <div className="route-outlet">
        <BackHeader
          title="编辑日程"
          action="back-to-schedule-detail"
          label="返回日程详情"
          onBack={() => routeAdapter.replaceScheduleDetail(scheduleId)}
        />
        <SectionLoading body="正在读取日程编辑信息。" />
      </div>
    );
  }

  if (detail.status === 'error') {
    return (
      <div className="route-outlet">
        <BackHeader
          title="编辑日程"
          action="back-to-schedule-detail"
          label="返回日程详情"
          onBack={() => routeAdapter.replaceScheduleDetail(scheduleId)}
        />
        <RecoverableReadError
          error={detail.error}
          retryAction="retry-schedule-edit"
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  if (canEditScheduleSettings && editOptions.status === 'error') {
    return (
      <div className="route-outlet">
        <BackHeader
          title="编辑日程"
          action="back-to-schedule-detail"
          label="返回日程详情"
          onBack={() => routeAdapter.replaceScheduleDetail(scheduleId)}
        />
        <RecoverableReadError
          error={editOptions.error}
          retryAction="retry-schedule-edit"
          onRetry={() => void editOptions.refetch()}
        />
      </div>
    );
  }

  if (!schedule || !draft) {
    return null;
  }

  const updateDraft = (nextDraft: ScheduleDraftInput): void => {
    setActionError(null);
    draftStore.setEditDraft(scheduleId, nextDraft);
  };
  const leaveEdit = (): void => {
    if (isEditDirty) {
      setShowDiscardDialog(true);
      return;
    }
    draftStore.clearEditDraft(scheduleId);
    routeAdapter.replaceScheduleDetail(scheduleId);
  };

  return (
    <div className="route-outlet">
      <BackHeader
        title="编辑日程"
        action="back-to-schedule-detail"
        label="返回日程详情"
        pending={updateSchedule.isPending}
        onBack={leaveEdit}
      />
      {showDiscardDialog ? (
        <DiscardUnsavedChangesDialog
          onKeepEditing={() => setShowDiscardDialog(false)}
          onDiscard={() => {
            setShowDiscardDialog(false);
            setSubmitNavigationAllowed(true);
            draftStore.clearEditDraft(scheduleId);
            routeAdapter.replaceScheduleDetail(scheduleId);
          }}
        />
      ) : null}
      <ScheduleEditorLeaveGuard
        flow={{ kind: 'edit', scheduleId }}
        isDirty={isEditDirty}
        isDisabled={isSubmitNavigationAllowed}
        onDiscard={() => draftStore.clearEditDraft(scheduleId)}
      />
      <ScheduleEditorContent
        draft={draft}
        editOptions={editOptions}
        currentAttendance={schedule.attendance}
        canEditScheduleSettings={canEditScheduleSettings}
        onDraftChange={updateDraft}
        onOpenTimePicker={() => routeAdapter.openScheduleEditTime(scheduleId)}
        onOpenAttendanceSettings={() => routeAdapter.openScheduleEditAttendance(scheduleId)}
        actionError={actionError}
        saveAction="save-schedule"
        savePending={updateSchedule.isPending}
        onSave={() => {
          void (async () => {
            setActionError(null);
            setSubmitNavigationAllowed(true);
            try {
              await updateSchedule.mutateAsync({ detail: schedule, draft });
            } catch (error) {
              setSubmitNavigationAllowed(false);
              setActionError(formatActionError(error));
            }
          })();
        }}
      />
    </div>
  );
}

export function ScheduleCreateTimePage(): ReactNode {
  const draftStore = useScheduleDraftStore();
  const routeAdapter = useWorkspaceRouteAdapter();
  const draft = draftStore.createDraft?.draft ?? null;
  const [childDirty, setChildDirty] = useState(false);

  return (
    <>
      <ScheduleEditorLeaveGuard
        flow={{ kind: 'create' }}
        isDirty={isCreateScheduleDraftDirty(draft) || childDirty}
        onDiscard={draftStore.clearCreateDraft}
      />
      <ScheduleTimeEditor
        draft={draft}
        onDirtyChange={setChildDirty}
        onBack={() => {
          if (draft) {
            routeAdapter.replaceScheduleCreate(draft);
            return;
          }
          routeAdapter.replaceScheduleCalendar();
        }}
        onSave={(nextDraft) => {
          draftStore.setCreateDraft(getScheduleDateKey(nextDraft.startsAt), nextDraft);
          routeAdapter.replaceScheduleCreate(nextDraft);
        }}
      />
    </>
  );
}

export function ScheduleEditTimePage({ scheduleId }: { readonly scheduleId: string }): ReactNode {
  const draftStore = useScheduleDraftStore();
  const routeAdapter = useWorkspaceRouteAdapter();
  const detail = useScheduleDetailQuery(scheduleId);
  const draft = draftStore.editDrafts.get(scheduleId) ?? null;
  const schedule = detail.status === 'success' ? detail.data : null;
  const editabilityNow = useScheduleEditabilityNow(schedule);
  const canEditScheduleSettings = schedule ? canEditScheduleBeforeStart(schedule, editabilityNow) : false;
  const [childDirty, setChildDirty] = useState(false);
  const isEditDirty = Boolean(schedule && draft && isScheduleDraftChanged(schedule, draft, canEditScheduleSettings));
  const leaveGuard = (
    <ScheduleEditorLeaveGuard
      flow={{ kind: 'edit', scheduleId }}
      isDirty={isEditDirty || (canEditScheduleSettings && childDirty)}
      onDiscard={() => draftStore.clearEditDraft(scheduleId)}
    />
  );

  if (detail.status === 'pending') {
    return (
      <>
        {leaveGuard}
        <ScheduleLockedChildRoute
          title="日程时间"
          message="正在读取日程详情。"
          onBack={() => routeAdapter.replaceScheduleEdit(scheduleId)}
        />
      </>
    );
  }

  if (detail.status === 'error') {
    return (
      <>
        {leaveGuard}
        <div className="route-outlet">
          <BackHeader
            title="日程时间"
            action="back-to-schedule-editor"
            label="返回日程编辑"
            onBack={() => routeAdapter.replaceScheduleEdit(scheduleId)}
          />
          <RecoverableReadError
            error={detail.error}
            retryAction="retry-schedule-time"
            onRetry={() => void detail.refetch()}
          />
        </div>
      </>
    );
  }

  if (!canEditScheduleSettings) {
    return (
      <>
        {leaveGuard}
        <ScheduleLockedChildRoute
          title="日程时间"
          message="日程已经开始，不能修改时间。"
          onBack={() => routeAdapter.replaceScheduleEdit(scheduleId)}
        />
      </>
    );
  }

  return (
    <>
      {leaveGuard}
      <ScheduleTimeEditor
        draft={draft}
        onDirtyChange={setChildDirty}
        onBack={() => routeAdapter.replaceScheduleEdit(scheduleId)}
        onSave={(nextDraft) => {
          draftStore.setEditDraft(scheduleId, nextDraft);
          routeAdapter.replaceScheduleEdit(scheduleId);
        }}
      />
    </>
  );
}

export function ScheduleCreateAttendancePage(): ReactNode {
  const draftStore = useScheduleDraftStore();
  const routeAdapter = useWorkspaceRouteAdapter();
  const draft = draftStore.createDraft?.draft ?? null;
  const [childDirty, setChildDirty] = useState(false);

  return (
    <>
      <ScheduleEditorLeaveGuard
        flow={{ kind: 'create' }}
        isDirty={isCreateScheduleDraftDirty(draft) || childDirty}
        onDiscard={draftStore.clearCreateDraft}
      />
      <ScheduleAttendanceSettingsEditor
        draft={draft}
        onDirtyChange={setChildDirty}
        onBack={() => {
          if (draft) {
            routeAdapter.replaceScheduleCreate(draft);
            return;
          }
          routeAdapter.replaceScheduleCalendar();
        }}
        onSave={(nextDraft) => {
          draftStore.setCreateDraft(getScheduleDateKey(nextDraft.startsAt), nextDraft);
          routeAdapter.replaceScheduleCreate(nextDraft);
        }}
      />
    </>
  );
}

export function ScheduleEditAttendancePage({ scheduleId }: { readonly scheduleId: string }): ReactNode {
  const draftStore = useScheduleDraftStore();
  const routeAdapter = useWorkspaceRouteAdapter();
  const detail = useScheduleDetailQuery(scheduleId);
  const draft = draftStore.editDrafts.get(scheduleId) ?? null;
  const schedule = detail.status === 'success' ? detail.data : null;
  const editabilityNow = useScheduleEditabilityNow(schedule);
  const canEditScheduleSettings = schedule ? canEditScheduleBeforeStart(schedule, editabilityNow) : false;
  const [childDirty, setChildDirty] = useState(false);
  const isEditDirty = Boolean(schedule && draft && isScheduleDraftChanged(schedule, draft, canEditScheduleSettings));
  const leaveGuard = (
    <ScheduleEditorLeaveGuard
      flow={{ kind: 'edit', scheduleId }}
      isDirty={isEditDirty || (canEditScheduleSettings && childDirty)}
      onDiscard={() => draftStore.clearEditDraft(scheduleId)}
    />
  );

  if (detail.status === 'pending') {
    return (
      <>
        {leaveGuard}
        <ScheduleLockedChildRoute
          title="考勤设置"
          message="正在读取日程详情。"
          onBack={() => routeAdapter.replaceScheduleEdit(scheduleId)}
        />
      </>
    );
  }

  if (detail.status === 'error') {
    return (
      <>
        {leaveGuard}
        <div className="route-outlet">
          <BackHeader
            title="考勤设置"
            action="back-to-schedule-editor"
            label="返回日程编辑"
            onBack={() => routeAdapter.replaceScheduleEdit(scheduleId)}
          />
          <RecoverableReadError
            error={detail.error}
            retryAction="retry-schedule-attendance-settings"
            onRetry={() => void detail.refetch()}
          />
        </div>
      </>
    );
  }

  if (!canEditScheduleSettings) {
    return (
      <>
        {leaveGuard}
        <ScheduleLockedChildRoute
          title="考勤设置"
          message="日程已经开始，不能修改考勤设置。"
          onBack={() => routeAdapter.replaceScheduleEdit(scheduleId)}
        />
      </>
    );
  }

  return (
    <>
      {leaveGuard}
      <ScheduleAttendanceSettingsEditor
        draft={draft}
        onDirtyChange={setChildDirty}
        onBack={() => routeAdapter.replaceScheduleEdit(scheduleId)}
        onSave={(nextDraft) => {
          draftStore.setEditDraft(scheduleId, nextDraft);
          routeAdapter.replaceScheduleEdit(scheduleId);
        }}
      />
    </>
  );
}

export function ScheduleDeletePage({ scheduleId }: { readonly scheduleId: string }): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const routeAdapter = useWorkspaceRouteAdapter();
  const detail = useScheduleDetailQuery(scheduleId);
  const [actionError, setActionError] = useState<string | null>(null);
  const deleteSchedule = useMutation({
    mutationFn: () => service.deleteSchedule({ workspaceContext, scheduleId }),
    onSuccess: async () => {
      const schedule = detail.data;
      const detailQueryKey = scheduleDetailQueryKey(workspaceContext, scheduleId);
      if (schedule) {
        await invalidateScheduleMonths(queryClient, workspaceContext, [
          formatScheduleMonthKey(getScheduleDateKey(schedule.startsAt))
        ]);
      }
      await invalidateScheduleUpcomingQueries(queryClient, workspaceContext);
      await queryClient.invalidateQueries({
        queryKey: scheduleHomeUpcomingQueryKey(workspaceContext),
        exact: true
      });
      await routeAdapter.backToScheduleRoot();
      queryClient.removeQueries({
        queryKey: detailQueryKey,
        exact: true
      });
    }
  });

  return (
    <div className="route-outlet">
      <BackHeader
        title="删除日程"
        action="back-to-schedule-detail"
        label="返回日程详情"
        pending={deleteSchedule.isPending}
        onBack={() => routeAdapter.replaceScheduleDetail(scheduleId)}
      />
      {detail.status === 'pending' ? <SectionLoading body="正在读取日程详情。" /> : null}
      {detail.status === 'error' ? (
        <RecoverableReadError
          error={detail.error}
          retryAction="retry-confirm-delete-schedule"
          onRetry={() => void detail.refetch()}
        />
      ) : null}
      {detail.status === 'success' && !detail.data.canManage ? (
        <section className="panel" role="alert" tabIndex={-1}>
          <p>无权删除这个日程。</p>
        </section>
      ) : null}
      {detail.status === 'success' && detail.data.canManage ? (
        <section className="confirm-card">
          <h2>{`删除“${detail.data.title}”？`}</h2>
          <p>删除后，日程会从常规日程流隐藏；既有考勤记录会保留在历史可见场景。</p>
          <ActionError message={actionError} />
          <div className="footer-actions">
            <button
              type="button"
              className="secondary-button"
              data-action="cancel-delete-schedule"
              disabled={deleteSchedule.isPending}
              onClick={() => routeAdapter.replaceScheduleDetail(scheduleId)}
            >
              取消
            </button>
            <PendingButton
              action="confirm-delete-schedule"
              label="确认删除"
              variant="secondary"
              pending={deleteSchedule.isPending}
              onClick={() => {
                void (async () => {
                  setActionError(null);
                  try {
                    await deleteSchedule.mutateAsync();
                  } catch (error) {
                    setActionError(formatActionError(error));
                  }
                })();
              }}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
}

function ScheduleEditorContent({
  draft,
  editOptions,
  currentAttendance,
  canEditScheduleSettings,
  onDraftChange,
  onOpenTimePicker,
  onOpenAttendanceSettings,
  actionError,
  saveAction,
  savePending,
  onSave
}: {
  readonly draft: ScheduleDraftInput;
  readonly editOptions: ReturnType<typeof useScheduleEditOptionsQuery>;
  readonly currentAttendance?: ScheduleDetail['attendance'];
  readonly canEditScheduleSettings: boolean;
  readonly onDraftChange: (draft: ScheduleDraftInput) => void;
  readonly onOpenTimePicker: () => void;
  readonly onOpenAttendanceSettings: () => void;
  readonly actionError: string | null;
  readonly saveAction: string;
  readonly savePending: boolean;
  readonly onSave: () => void;
}): ReactNode {
  if (canEditScheduleSettings && editOptions.status === 'pending') {
    return <SectionLoading body="正在读取日程编辑选项。" />;
  }

  if (canEditScheduleSettings && editOptions.status === 'error') {
    return (
      <RecoverableReadError
        error={editOptions.error}
        retryAction="retry-schedule-edit"
        onRetry={() => void editOptions.refetch()}
      />
    );
  }

  const attendanceSummary = formatEditorAttendanceSummary(
    draft,
    editOptions.data ?? { attendanceCategories: [], participantMemberGroups: [] },
    currentAttendance
  );

  return (
    <>
      <section className="schedule-editor-panel">
        <TextField
          label="标题"
          field="schedule-title"
          value={draft.title}
          onChange={(title) => onDraftChange({ ...draft, title })}
        />
        <ScheduleEditorSummaryRow
          label="时间"
          primary={formatEditorTimeRange(draft)}
          action={canEditScheduleSettings ? 'open-schedule-time-picker' : undefined}
          onClick={canEditScheduleSettings ? onOpenTimePicker : undefined}
        />
        <TextField
          label="地点"
          field="schedule-location"
          value={draft.location}
          onChange={(location) => onDraftChange({ ...draft, location })}
        />
        <TextareaField
          label="备注"
          field="schedule-note"
          value={draft.note ?? ''}
          onChange={(note) => onDraftChange({ ...draft, note })}
        />
        <ScheduleEditorSummaryRow
          label="考勤"
          primary={attendanceSummary.category}
          secondary={[attendanceSummary.scope, attendanceSummary.reference]}
          action={canEditScheduleSettings ? 'open-schedule-attendance-settings' : undefined}
          onClick={canEditScheduleSettings ? onOpenAttendanceSettings : undefined}
        />
      </section>
      <ActionError message={actionError} />
      <div className="footer-actions">
        <PendingButton action={saveAction} label="保存" variant="primary" pending={savePending} onClick={onSave} />
      </div>
    </>
  );
}

function ScheduleTimeEditor({
  draft,
  onDirtyChange,
  onBack,
  onSave
}: {
  readonly draft: ScheduleDraftInput | null;
  readonly onDirtyChange?: (isDirty: boolean) => void;
  readonly onBack: () => void;
  readonly onSave: (draft: ScheduleDraftInput) => void;
}): ReactNode {
  const [startTime, setStartTime] = useState(draft ? formatEditorTime(draft.startsAt) : '19:00');
  const [endTime, setEndTime] = useState(draft ? formatEditorTime(draft.endsAt) : '20:00');
  const isTimeDirty = Boolean(
    draft && (startTime !== formatEditorTime(draft.startsAt) || endTime !== formatEditorTime(draft.endsAt))
  );

  useEffect(() => {
    onDirtyChange?.(isTimeDirty);
  }, [isTimeDirty, onDirtyChange]);

  return (
    <div className="route-outlet">
      <BackHeader title="日程时间" action="back-to-schedule-editor" label="返回日程编辑" onBack={onBack} />
      {!draft ? (
        <section className="panel error" role="alert" tabIndex={-1}>
          <p>请先返回日程编辑页设置时间。</p>
        </section>
      ) : (
        <>
          <section className="schedule-time-panel">
            <TextField
              label="开始时间"
              field="schedule-start-time"
              type="time"
              value={startTime}
              onChange={setStartTime}
            />
            <TextField label="结束时间" field="schedule-end-time" type="time" value={endTime} onChange={setEndTime} />
          </section>
          <div className="footer-actions">
            <button
              type="button"
              className="primary-button"
              data-action="save-schedule-time"
              onClick={() => {
                const dateKey = getScheduleDateKey(draft.startsAt);
                onSave({
                  ...draft,
                  startsAt: toUtcDateTime(dateKey, startTime),
                  endsAt: toUtcDateTime(dateKey, endTime)
                });
              }}
            >
              保存时间
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ScheduleAttendanceSettingsEditor({
  draft,
  onDirtyChange,
  onBack,
  onSave
}: {
  readonly draft: ScheduleDraftInput | null;
  readonly onDirtyChange?: (isDirty: boolean) => void;
  readonly onBack: () => void;
  readonly onSave: (draft: ScheduleDraftInput) => void;
}): ReactNode {
  const editOptions = useScheduleEditOptionsQuery(Boolean(draft));
  const [actionError, setActionError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(Boolean(draft?.attendance));
  const [categoryId, setCategoryId] = useState(draft?.attendance?.attendanceCategoryId ?? '');
  const [participantIds, setParticipantIds] = useState<readonly string[]>(
    draft?.attendance?.participantMemberGroupIds ?? []
  );
  const [onTimeReference, setOnTimeReference] = useState(
    String(draft?.attendance?.onTimeReferenceMinutesFromStart ?? 0)
  );
  const [lateReference, setLateReference] = useState(String(draft?.attendance?.lateReferenceMinutesFromStart ?? 60));
  const isAttendanceDirty = draft
    ? isScheduleAttendanceEditorDirty(draft, enabled, categoryId, participantIds, onTimeReference, lateReference)
    : false;

  useEffect(() => {
    onDirtyChange?.(isAttendanceDirty);
  }, [isAttendanceDirty, onDirtyChange]);

  if (!draft) {
    return (
      <div className="route-outlet">
        <BackHeader title="考勤设置" action="back-to-schedule-editor" label="返回日程编辑" onBack={onBack} />
        <section className="panel error" role="alert" tabIndex={-1}>
          <p>请先返回日程编辑页设置考勤。</p>
        </section>
      </div>
    );
  }

  if (editOptions.status === 'pending') {
    return (
      <div className="route-outlet">
        <BackHeader title="考勤设置" action="back-to-schedule-editor" label="返回日程编辑" onBack={onBack} />
        <SectionLoading body="正在读取考勤设置选项。" />
      </div>
    );
  }

  if (editOptions.status === 'error') {
    return (
      <div className="route-outlet">
        <BackHeader title="考勤设置" action="back-to-schedule-editor" label="返回日程编辑" onBack={onBack} />
        <RecoverableReadError
          error={editOptions.error}
          retryAction="retry-schedule-attendance-settings"
          onRetry={() => void editOptions.refetch()}
        />
      </div>
    );
  }

  const options = editOptions.data;

  return (
    <div className="route-outlet">
      <BackHeader title="考勤设置" action="back-to-schedule-editor" label="返回日程编辑" onBack={onBack} />
      <ActionError message={actionError} />
      <section className="schedule-attendance-panel">
        <label className="schedule-switch-row" data-field="schedule-attendance-enabled">
          <span>
            <strong>启用考勤</strong>
          </span>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => {
              setActionError(null);
              if (!event.currentTarget.checked) {
                setEnabled(false);
                return;
              }
              if (options.attendanceCategories.length === 0) {
                setActionError('请先启用一个考勤类别。');
                setEnabled(false);
                return;
              }
              const nextAttendance = buildDefaultAttendanceDraft(options, draft.attendance);
              setEnabled(true);
              setCategoryId(nextAttendance.attendanceCategoryId);
              setParticipantIds(nextAttendance.participantMemberGroupIds);
              setOnTimeReference(String(nextAttendance.onTimeReferenceMinutesFromStart));
              setLateReference(String(nextAttendance.lateReferenceMinutesFromStart));
            }}
          />
        </label>
        {enabled ? (
          <>
            <label data-field="schedule-attendance-category">
              <span>考勤类别</span>
              <select value={categoryId} onChange={(event) => setCategoryId(event.currentTarget.value)}>
                {options.attendanceCategories.map((category) => (
                  <option value={category.attendanceCategoryId} key={category.attendanceCategoryId}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
            <fieldset>
              <legend>参与范围</legend>
              {options.participantMemberGroups.map((group) => (
                <label data-field="schedule-participant-member-group" key={group.memberGroupId}>
                  <input
                    type="checkbox"
                    value={group.memberGroupId}
                    checked={participantIds.includes(group.memberGroupId)}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      setParticipantIds((current) =>
                        checked
                          ? [...current, group.memberGroupId]
                          : current.filter((memberGroupId) => memberGroupId !== group.memberGroupId)
                      );
                    }}
                  />
                  {group.name}
                </label>
              ))}
            </fieldset>
            <TextField
              label="准时参考（分钟）"
              field="schedule-on-time-reference"
              type="number"
              value={onTimeReference}
              onChange={setOnTimeReference}
            />
            <TextField
              label="迟到参考（分钟）"
              field="schedule-late-reference"
              type="number"
              value={lateReference}
              onChange={setLateReference}
            />
          </>
        ) : null}
      </section>
      <div className="footer-actions">
        <button
          type="button"
          className="primary-button"
          data-action="save-schedule-attendance-settings"
          onClick={() => {
            if (!enabled) {
              onSave({ ...draft, attendance: null });
              return;
            }
            if (!categoryId) {
              setActionError('请先启用一个考勤类别。');
              return;
            }
            onSave({
              ...draft,
              attendance: {
                attendanceCategoryId: categoryId,
                participantMemberGroupIds: participantIds,
                onTimeReferenceMinutesFromStart: Number(onTimeReference),
                lateReferenceMinutesFromStart: Number(lateReference)
              }
            });
          }}
        >
          保存考勤设置
        </button>
      </div>
    </div>
  );
}

function ScheduleEditorLeaveGuard({
  flow,
  isDirty,
  isDisabled = false,
  onDiscard
}: {
  readonly flow: { readonly kind: 'create' } | { readonly kind: 'edit'; readonly scheduleId: string };
  readonly isDirty: boolean;
  readonly isDisabled?: boolean;
  readonly onDiscard: () => void;
}): ReactNode {
  const blocker = useScheduleEditorLeaveBlocker({
    flow,
    isDirty,
    isDisabled
  });

  if (blocker.status !== 'blocked') {
    return null;
  }

  return (
    <DiscardUnsavedChangesDialog
      onKeepEditing={blocker.reset}
      onDiscard={() => {
        onDiscard();
        blocker.proceed();
      }}
    />
  );
}

function ScheduleLockedChildRoute({
  title,
  message,
  onBack
}: {
  readonly title: string;
  readonly message: string;
  readonly onBack: () => void;
}): ReactNode {
  return (
    <div className="route-outlet">
      <BackHeader title={title} action="back-to-schedule-editor" label="返回日程编辑" onBack={onBack} />
      <section className="panel" role="status">
        <p>{message}</p>
      </section>
    </div>
  );
}

function ScheduleEditorSummaryRow({
  label,
  primary,
  secondary = [],
  action,
  onClick
}: {
  readonly label: string;
  readonly primary: string;
  readonly secondary?: readonly string[];
  readonly action?: string;
  readonly onClick?: () => void;
}): ReactNode {
  const content = (
    <span className="schedule-editor-summary-copy">
      <span>{label}</span>
      <strong>{primary}</strong>
      {secondary.map((item) => (
        <small key={item}>{item}</small>
      ))}
    </span>
  );

  if (!action || !onClick) {
    return <div className="schedule-editor-summary-row">{content}</div>;
  }

  return (
    <button type="button" className="schedule-editor-summary-row" data-action={action} onClick={onClick}>
      {content}
      <span className="choir-context-chevron" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

function TextareaField({
  label,
  field,
  value,
  onChange
}: {
  readonly label: string;
  readonly field: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}): ReactNode {
  return (
    <label className="field" data-field={field}>
      <span>{label}</span>
      <textarea value={value} onChange={(event) => onChange(event.currentTarget.value)} />
    </label>
  );
}

function useScheduleEditabilityNow(schedule: ScheduleDetail | null): number {
  const [now, setNow] = useState(() => Date.now());
  const startsAt = schedule?.startsAt;

  useEffect(() => {
    if (!startsAt) {
      return;
    }

    const startsAtMillis = parseScheduleTimestamp(startsAt);
    const delay = startsAtMillis - Date.now();
    if (delay <= 0) {
      setNow(Date.now());
      return;
    }

    const timeout = window.setTimeout(
      () => {
        setNow(Date.now());
      },
      Math.min(delay, MAX_TIMER_DELAY_MS)
    );
    return () => window.clearTimeout(timeout);
  }, [startsAt]);

  return now;
}

function useScheduleEditorLeaveBlocker({
  isDirty,
  isDisabled,
  flow
}: {
  readonly isDirty: boolean;
  readonly isDisabled: boolean;
  readonly flow: { readonly kind: 'create' } | { readonly kind: 'edit'; readonly scheduleId: string };
}) {
  return useBlocker({
    withResolver: true,
    enableBeforeUnload: () => isDirty && !isDisabled,
    disabled: !isDirty || isDisabled,
    shouldBlockFn: ({ current, next }) => {
      if (!isScheduleEditorFlowPath(current.pathname, flow)) {
        return false;
      }

      return !isScheduleEditorFlowPath(next.pathname, flow);
    }
  });
}

function isScheduleEditorFlowPath(
  pathname: string,
  flow: { readonly kind: 'create' } | { readonly kind: 'edit'; readonly scheduleId: string }
): boolean {
  if (flow.kind === 'create') {
    return /^\/workspace\/schedules\/new(?:\/(?:time|attendance))?$/.test(pathname);
  }

  const escapedScheduleId = escapeRegExp(flow.scheduleId);
  return new RegExp(`^/workspace/schedules/${escapedScheduleId}/edit(?:/(?:time|attendance))?$`).test(pathname);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getScheduleMonthKeyFromSearch(search: WorkspaceScheduleTabSearch): string {
  if (search.month) {
    return search.month;
  }

  if (search.date) {
    return formatScheduleMonthKey(search.date);
  }

  return formatScheduleMonthKey(formatDateKey(new Date()));
}

function isDateKey(value: unknown): value is string {
  if (typeof value !== 'string' || !DATE_KEY_PATTERN.test(value)) {
    return false;
  }

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day &&
    formatDateKey(date) === value
  );
}

function formatAttendanceReferenceTime(startsAt: string, minutesFromStart: number): string {
  const startsAtMillis = parseScheduleTimestamp(startsAt);
  const reference = new Date(startsAtMillis + minutesFromStart * 60 * 1000);
  return `${String(reference.getUTCHours()).padStart(2, '0')}:${String(reference.getUTCMinutes()).padStart(2, '0')}`;
}

function parseScheduleTimestamp(timestamp: string): number {
  const millis = Date.parse(timestamp);
  if (Number.isNaN(millis)) {
    throw new Error(`Invalid schedule timestamp: ${timestamp}`);
  }
  return millis;
}

function buildCreateScheduleDraft(selectedDate: string): ScheduleDraftInput {
  return {
    title: '',
    startsAt: toUtcDateTime(selectedDate, '19:00'),
    endsAt: toUtcDateTime(selectedDate, '20:00'),
    location: '',
    note: '',
    attendance: null
  };
}

function buildEditScheduleDraft(detail: ScheduleDetail): ScheduleDraftInput {
  return {
    title: detail.title,
    startsAt: detail.startsAt,
    endsAt: detail.endsAt,
    location: detail.location,
    note: detail.note,
    attendance: detail.attendance
      ? {
          attendanceCategoryId: detail.attendance.category.attendanceCategoryId,
          participantMemberGroupIds: detail.attendance.participantMemberGroupIds,
          onTimeReferenceMinutesFromStart: detail.attendance.onTimeReferenceMinutesFromStart,
          lateReferenceMinutesFromStart: detail.attendance.lateReferenceMinutesFromStart
        }
      : null
  };
}

function canEditScheduleBeforeStart(detail: ScheduleDetail, now = Date.now()): boolean {
  if (parseScheduleTimestamp(detail.startsAt) <= now) {
    return false;
  }

  return detail.attendanceStatus === 'not_started' || detail.attendanceStatus === 'not_enabled';
}

function buildScheduleUpdateDraftForEdit(
  detail: ScheduleDetail,
  draft: ScheduleDraftInput,
  canEditScheduleSettings: boolean
): ScheduleUpdateDraftInput {
  let patch: ScheduleUpdateDraftInput = {
    title: draft.title,
    location: draft.location,
    note: draft.note
  };

  if (!canEditScheduleSettings) {
    return patch;
  }

  if (draft.startsAt !== detail.startsAt || draft.endsAt !== detail.endsAt) {
    patch = {
      ...patch,
      startsAt: draft.startsAt,
      endsAt: draft.endsAt
    };
  }

  if (isScheduleAttendanceChanged(detail.attendance, draft.attendance)) {
    patch = {
      ...patch,
      attendance: draft.attendance
    };
  }

  return patch;
}

function isScheduleDraftChanged(
  detail: ScheduleDetail,
  draft: ScheduleDraftInput,
  canEditScheduleSettings: boolean
): boolean {
  return (
    draft.title !== detail.title ||
    draft.location !== detail.location ||
    (draft.note ?? '') !== detail.note ||
    (canEditScheduleSettings &&
      (draft.startsAt !== detail.startsAt ||
        draft.endsAt !== detail.endsAt ||
        isScheduleAttendanceChanged(detail.attendance, draft.attendance)))
  );
}

function isCreateScheduleDraftDirty(draft: ScheduleDraftInput | null): boolean {
  if (!draft) {
    return false;
  }
  return isScheduleDraftInputChanged(buildCreateScheduleDraft(getScheduleDateKey(draft.startsAt)), draft);
}
function isScheduleAttendanceEditorDirty(
  draft: ScheduleDraftInput,
  enabled: boolean,
  categoryId: string,
  participantIds: readonly string[],
  onTimeReference: string,
  lateReference: string
): boolean {
  const baseline = draft.attendance;
  if (!enabled) {
    return baseline !== null;
  }
  if (!baseline) {
    return true;
  }

  return (
    baseline.attendanceCategoryId !== categoryId ||
    !areStringArraysEqual(baseline.participantMemberGroupIds, participantIds) ||
    String(baseline.onTimeReferenceMinutesFromStart) !== onTimeReference ||
    String(baseline.lateReferenceMinutesFromStart) !== lateReference
  );
}

function isScheduleDraftInputChanged(baseline: ScheduleDraftInput, draft: ScheduleDraftInput): boolean {
  return (
    baseline.title !== draft.title ||
    baseline.location !== draft.location ||
    (baseline.note ?? '') !== (draft.note ?? '') ||
    baseline.startsAt !== draft.startsAt ||
    baseline.endsAt !== draft.endsAt ||
    isScheduleDraftAttendanceChanged(baseline.attendance, draft.attendance)
  );
}

function isScheduleDraftAttendanceChanged(
  baseline: ScheduleDraftInput['attendance'],
  draft: ScheduleDraftInput['attendance']
): boolean {
  if (!baseline && !draft) {
    return false;
  }
  if (!baseline || !draft) {
    return true;
  }
  return (
    baseline.attendanceCategoryId !== draft.attendanceCategoryId ||
    baseline.onTimeReferenceMinutesFromStart !== draft.onTimeReferenceMinutesFromStart ||
    baseline.lateReferenceMinutesFromStart !== draft.lateReferenceMinutesFromStart ||
    !areStringArraysEqual(baseline.participantMemberGroupIds, draft.participantMemberGroupIds)
  );
}

function isScheduleAttendanceChanged(
  existing: ScheduleDetail['attendance'],
  draft: ScheduleDraftInput['attendance']
): boolean {
  if (!existing && !draft) {
    return false;
  }
  if (!existing || !draft) {
    return true;
  }
  return (
    existing.category.attendanceCategoryId !== draft.attendanceCategoryId ||
    existing.onTimeReferenceMinutesFromStart !== draft.onTimeReferenceMinutesFromStart ||
    existing.lateReferenceMinutesFromStart !== draft.lateReferenceMinutesFromStart ||
    !areStringArraysEqual(existing.participantMemberGroupIds, draft.participantMemberGroupIds)
  );
}

function areStringArraysEqual(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function buildDefaultAttendanceDraft(
  options: ScheduleEditOptions,
  existing: ScheduleDraftInput['attendance']
): NonNullable<ScheduleDraftInput['attendance']> {
  if (existing) {
    return existing;
  }

  const category = options.attendanceCategories[0];
  if (!category) {
    throw new Error('Enabled attendance category is required');
  }

  return {
    attendanceCategoryId: category.attendanceCategoryId,
    participantMemberGroupIds: options.participantMemberGroups
      .filter((group) => group.isDefault)
      .map((group) => group.memberGroupId),
    onTimeReferenceMinutesFromStart: 0,
    lateReferenceMinutesFromStart: 60
  };
}

function formatEditorTimeRange(draft: Pick<ScheduleDraftInput, 'startsAt' | 'endsAt'>): string {
  return `${formatEditorTime(draft.startsAt)}-${formatEditorTime(draft.endsAt)}`;
}

function formatEditorTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid schedule timestamp: ${isoTimestamp}`);
  }

  return `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`;
}

function toUtcDateTime(dateKey: string, time: string): string {
  if (!isDateKey(dateKey) || !/^\d{2}:\d{2}$/.test(time)) {
    throw new Error('Schedule date and time are required');
  }

  return `${dateKey}T${time}:00.000Z`;
}

function formatEditorAttendanceSummary(
  draft: ScheduleDraftInput,
  options: ScheduleEditOptions,
  currentAttendance?: ScheduleDetail['attendance']
): { readonly category: string; readonly scope: string; readonly reference: string } {
  if (!draft.attendance) {
    return {
      category: '未设置',
      scope: '参与范围未设置',
      reference: '参考时间未设置'
    };
  }

  return {
    category: findEditorAttendanceCategoryName(
      options,
      draft.attendance.attendanceCategoryId,
      currentAttendance?.category
    ),
    scope: formatEditorParticipantGroupNames(
      options,
      draft.attendance.participantMemberGroupIds,
      currentAttendance?.participantMemberGroups
    ),
    reference: `准时 ${draft.attendance.onTimeReferenceMinutesFromStart} 分钟 · 迟到 ${draft.attendance.lateReferenceMinutesFromStart} 分钟`
  };
}

function findEditorAttendanceCategoryName(
  options: ScheduleEditOptions,
  attendanceCategoryId: string,
  currentCategory?: NonNullable<ScheduleDetail['attendance']>['category']
): string {
  if (currentCategory?.attendanceCategoryId === attendanceCategoryId) {
    return currentCategory.name;
  }
  const category = options.attendanceCategories.find(
    (candidate) => candidate.attendanceCategoryId === attendanceCategoryId
  );
  if (!category) {
    throw new Error('Attendance category not found');
  }
  return category.name;
}

function formatEditorParticipantGroupNames(
  options: ScheduleEditOptions,
  memberGroupIds: readonly string[],
  currentGroups: readonly { readonly memberGroupId: string; readonly name: string }[] = []
): string {
  if (memberGroupIds.length === 0) {
    return '全团';
  }

  return memberGroupIds
    .map((memberGroupId) => findEditorParticipantGroupName(options, currentGroups, memberGroupId))
    .join('、');
}

function findEditorParticipantGroupName(
  options: ScheduleEditOptions,
  currentGroups: readonly { readonly memberGroupId: string; readonly name: string }[],
  memberGroupId: string
): string {
  const option = options.participantMemberGroups.find((candidate) => candidate.memberGroupId === memberGroupId);
  if (option) {
    return option.name;
  }
  const currentGroup = currentGroups.find((candidate) => candidate.memberGroupId === memberGroupId);
  if (currentGroup) {
    return currentGroup.name;
  }
  throw new Error('Schedule participant member group not found');
}

function formatParticipantMemberGroups(memberGroups: readonly { readonly name: string }[]): string {
  return memberGroups.length === 0 ? '全团' : memberGroups.map((memberGroup) => memberGroup.name).join('、');
}
